import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import parse, {
  DOMNode,
  Element,
  Text,
  attributesToProps,
  domToReact,
} from 'html-react-parser';
import { Fragment, type CSSProperties, type ReactNode } from 'react';

import { IEdition } from 'src/interfaces/Edition';
import DisplayHeadingWithRule from 'src/common/components/DisplayHeadingWithRule';
import Icon, { IconProps } from 'src/common/components/icons/Icon';
import { parseIconVisualFromClass } from 'src/common/components/icons/iconPicker';
import RichTextImage from 'src/common/components/RichTextImage';
import { sanitizeImageHtml } from 'src/common/components/tinymce/editorImageSanitize';
import MaterialIcon from 'src/common/components/icons/MaterialIcon';
import {
  materialIconIdFromAttr,
  materialIconIdFromTag,
  materialIconTagName,
} from 'src/common/components/icons/materialIconRegistry';
import { dividerSpacingSx, normalizeDividerSpacing } from 'src/helpers/dividerSpacing';
import {
  richTextCalloutPaperSx,
  richTextTableInnerContainerSx,
  richTextTableOuterSx,
  richTextTablePaperSx,
  richTextTableStripedSx,
  tableCellSxFromAttribs,
  tableContainerSxFromAttribs,
  tableRowSxFromAttribs,
  tableSxFromAttribs,
} from 'src/helpers/tinymceTableStyles';
import {
  listSxFromAttribs,
  partitionListItemChildren,
  RICH_TEXT_LIST_ITEM_ICON_CLASS,
  richTextListItemSx,
  richTextListOuterSx,
  richTextListRootSx,
  richTextNestedListSx,
} from 'src/helpers/tinymceListStyles';
import {
  applyTextPlaceholders,
  preprocessPlaceholders,
  renderPlaceholderInText,
  renderPlaceholderMarker,
  type PlaceholderRenderContext,
} from 'src/helpers/tinymcePlaceholderRegistry';
import { isLeftAlignedHeading } from 'src/common/styles/displayHeadingRuleStyles';
import { richTextBlockSpacingSx } from 'src/helpers/richTextSpacing';

// html-react-parser v6: use `type` checks; `instanceof Text/Element` fails due to duplicate domhandler copies
const isTextNode = (node: DOMNode): node is Text => node.type === 'text';

const isElementNode = (node: DOMNode): node is Element => node.type === 'tag';

/** Matches html-react-parser replace() return type (fork-ts-checker is stricter than tsc alone). */
type ParserReplaceResult = string | boolean | void | object | Element | null;

const TEXT_ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify', 'start', 'end']);

function parseInlineStyle(style?: string): CSSProperties {
  if (!style) return {};
  const result: CSSProperties = {};
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) continue;
    const prop = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (!value) continue;
    if (prop === 'text-align') {
      result.textAlign = value as CSSProperties['textAlign'];
    }
  }
  return result;
}

function alignmentSxFromAttribs(attribs: Record<string, string>): CSSProperties {
  const sx = parseInlineStyle(attribs.style);
  if (sx.textAlign) return sx;

  const align = attribs.align?.toLowerCase();
  if (align && TEXT_ALIGN_VALUES.has(align)) {
    return { textAlign: align as CSSProperties['textAlign'] };
  }
  return {};
}

function typographyBlockSx(
  attribs: Record<string, string>,
  mb: number,
  lineHeight?: number,
): SxProps<Theme> {
  return {
    ...richTextBlockSpacingSx(mb),
    ...alignmentSxFromAttribs(attribs),
    ...(lineHeight != null ? { lineHeight } : {}),
  } as SxProps<Theme>;
}

function headingTrailingRuleDisabled(attribs: Record<string, string>): boolean {
  const { textAlign } = alignmentSxFromAttribs(attribs);
  return !isLeftAlignedHeading(textAlign);
}

function tableCellFromAttribs(attribs: Record<string, string>, head = false) {
  const { style, class: className, ...rest } = attributesToProps(attribs);
  void style;
  return {
    ...rest,
    className: typeof className === 'string' ? className : undefined,
    sx: tableCellSxFromAttribs(attribs, head),
  };
}

const PLACEHOLDER_ATTR = 'data-ttt-placeholder';
const LEGACY_COMPETITIONS_LIST_ATTR = 'data-ttt-competitions-list';

function elementHasPlaceholderMarker(attribs: Record<string, string>): boolean {
  return (
    attribs[PLACEHOLDER_ATTR] !== undefined || attribs[LEGACY_COMPETITIONS_LIST_ATTR] !== undefined
  );
}

/** Image-only paragraphs should not render as body text blocks. */
function paragraphContainsOnlyImages(element: Element): boolean {
  let hasImage = false;

  if (!element.children.length) return false;

  for (const child of element.children as DOMNode[]) {
    if (isTextNode(child)) {
      if (child.data.trim()) return false;
      continue;
    }
    if (isElementNode(child)) {
      if (child.name === 'br') continue;
      if (child.name === 'img') {
        hasImage = true;
        continue;
      }
      return false;
    }
    return false;
  }

  return hasImage;
}

/** Block placeholders must not be wrapped in `<p>` (invalid HTML breaks MUI Grid layout). */
function paragraphContainsOnlyPlaceholders(element: Element): boolean {
  if (!element.children.length) return false;
  return (element.children as DOMNode[]).every(child => {
    if (isTextNode(child)) {
      return !child.data.trim();
    }
    if (isElementNode(child)) {
      return elementHasPlaceholderMarker(child.attribs ?? {});
    }
    return false;
  });
}

type ParserOptions = Parameters<typeof domToReact>[1];

function renderRichTextNestedList(nested: Element, options: ParserOptions): ReactNode {
  const ordered = nested.name === 'ol';
  return (
    <List
      component={ordered ? 'ol' : 'ul'}
      disablePadding
      dense
      sx={[richTextNestedListSx, richTextListRootSx(ordered)] as SxProps<Theme>}
    >
      {domToReact(nested.children as DOMNode[], options)}
    </List>
  );
}

function getParentListOrdered(element: Element): boolean {
  let node: DOMNode | null = element.parent as DOMNode | null;
  while (node && isElementNode(node)) {
    if (node.name === 'ol') return true;
    if (node.name === 'ul') return false;
    node = node.parent as DOMNode | null;
  }
  return false;
}

function renderMaterialIconNodeFromAttr(
  iconAttr: string,
  attribs: Record<string, string> = {},
): ReactNode | null {
  const iconId = materialIconIdFromAttr(iconAttr);
  if (!iconId) return null;
  return renderMaterialIconNode(materialIconTagName(iconId), attribs);
}

function renderMaterialIconNode(
  tagName: string,
  attribs: Record<string, string> = {},
): ReactNode | null {
  const iconId = materialIconIdFromTag(tagName);
  if (!iconId) return null;
  const iconVisual = parseIconVisualFromClass(attribs.class ?? '');
  const { class: _class, style: _style, ...iconProps } = attributesToProps(attribs);
  return (
    <MaterialIcon
      iconId={iconId}
      {...iconProps}
      sx={{
        fontSize: iconVisual.fontSize,
        color: iconVisual.color,
        verticalAlign: 'middle',
        display: 'inline-block',
      }}
    />
  );
}

function renderRichTextListItem(element: Element, options: ParserOptions): ReactNode {
  const { iconId, materialIconTag, iconAttribs, nestedLists, contentNodes } =
    partitionListItemChildren(element.children as DOMNode[], element.attribs);
  const hasPrimary = contentNodes.length > 0;
  const hasNested = nestedLists.length > 0;
  const ordered = getParentListOrdered(element);
  const hasListIcon = Boolean(iconId || materialIconTag);
  const iconVisual = hasListIcon
    ? parseIconVisualFromClass(iconAttribs?.class ?? 'icon icon-medium icon-primary')
    : null;

  const primaryText = hasPrimary ? (
    <ListItemText
      disableTypography
      primary={
        <Box component="span" sx={{ typography: 'body1', lineHeight: 1.75, display: 'inline' }}>
          {domToReact(contentNodes as DOMNode[], options)}
        </Box>
      }
      sx={{ m: 0, flex: hasListIcon || hasNested ? 1 : undefined, minWidth: 0 }}
    />
  ) : null;

  const nested = nestedLists.map((nestedEl, index) => (
    <Fragment key={`${nestedEl.name}-${index}`}>
      {renderRichTextNestedList(nestedEl, options)}
    </Fragment>
  ));

  return (
    <ListItem
      component="li"
      disableGutters
      className={hasListIcon ? RICH_TEXT_LIST_ITEM_ICON_CLASS : undefined}
      sx={[
        richTextListItemSx(element.attribs, { hasIcon: hasListIcon, ordered }),
        hasNested ? { flexDirection: 'column', alignItems: 'stretch' } : null,
      ] as SxProps<Theme>}
    >
      {hasListIcon && iconVisual ? (
        <ListItemIcon
          sx={{
            minWidth: 40,
            mt: 0.125,
            color: iconVisual.color,
            alignSelf: 'flex-start',
          }}
        >
          {materialIconTag ? (
            renderMaterialIconNode(materialIconTag, iconAttribs ?? {})
          ) : iconId ? (
            <Icon
              icon={iconId}
              sx={{
                fontSize: iconVisual.fontSize,
                color: 'inherit',
                display: 'block',
              }}
            />
          ) : null}
        </ListItemIcon>
      ) : null}
      {hasNested ? (
        <Box component="div" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {primaryText}
          {nested}
        </Box>
      ) : (
        <>
          {primaryText}
          {nested}
        </>
      )}
    </ListItem>
  );
}

export const parseTinyMCEContent = (
  content: string | null | undefined,
  extraArgs: {
    currentEdition?: IEdition;
    dateFormat?: string;
  } = {},
) => {
  if (!content || typeof content !== 'string') {
    return null;
  }

  const { currentEdition, dateFormat } = extraArgs;
  const html = sanitizeImageHtml(preprocessPlaceholders(content));
  const renderContext: PlaceholderRenderContext = { currentEdition, dateFormat };

  const options = {
    replace(domNode: DOMNode): ParserReplaceResult {
      if (isTextNode(domNode) && domNode.data) {
        const placeholderNode = renderPlaceholderInText(domNode.data, renderContext);
        if (placeholderNode != null) {
          return placeholderNode as object;
        }

        const replaced = applyTextPlaceholders(domNode.data, renderContext);
        if (replaced !== domNode.data) {
          domNode.data = replaced;
        }
        return domNode;
      }

      if (!isElementNode(domNode) || !domNode.attribs) {
        return;
      }

      const element: Element = domNode;

      if (elementHasPlaceholderMarker(element.attribs)) {
        const rendered = renderPlaceholderMarker(element, renderContext);
        return rendered != null ? (rendered as object) : null;
      }

      const renderedChildren = () => domToReact(element.children as DOMNode[], options);

      switch (element.name) {
        case 'h1':
          return (
            <Typography variant="h1" component="h1" sx={typographyBlockSx(element.attribs, 3)}>
              {renderedChildren()}
            </Typography>
          ) as object;
        case 'h2':
          return (
            headingTrailingRuleDisabled(element.attribs) ? (
              <Typography
                variant="h2"
                component="h2"
                sx={[
                  typographyBlockSx(element.attribs, 2.5),
                  {
                    display: 'block',
                    width: 'auto',
                    alignSelf: 'auto',
                    '&::after': { content: 'none', display: 'none' },
                  },
                ] as SxProps<Theme>}
              >
                {renderedChildren()}
              </Typography>
            ) : (
              <DisplayHeadingWithRule
                variant="h2"
                component="h2"
                sx={typographyBlockSx(element.attribs, 2.5)}
              >
                {renderedChildren()}
              </DisplayHeadingWithRule>
            )
          ) as object;
        case 'h3':
          return (
            headingTrailingRuleDisabled(element.attribs) ? (
              <Typography
                variant="h3"
                component="h3"
                sx={[
                  typographyBlockSx(element.attribs, 2),
                  {
                    display: 'block',
                    width: 'auto',
                    alignSelf: 'auto',
                    '&::after': { content: 'none', display: 'none' },
                  },
                ] as SxProps<Theme>}
              >
                {renderedChildren()}
              </Typography>
            ) : (
              <DisplayHeadingWithRule
                variant="h3"
                component="h3"
                sx={typographyBlockSx(element.attribs, 2)}
              >
                {renderedChildren()}
              </DisplayHeadingWithRule>
            )
          ) as object;
        case 'h4':
          return (
            <Typography variant="h4" component="h4" sx={typographyBlockSx(element.attribs, 2)}>
              {renderedChildren()}
            </Typography>
          ) as object;
        case 'h5':
          return (
            <Typography variant="h5" component="h5" sx={typographyBlockSx(element.attribs, 1.5)}>
              {renderedChildren()}
            </Typography>
          ) as object;
        case 'h6':
          return (
            <Typography variant="h6" component="h6" sx={typographyBlockSx(element.attribs, 1.5)}>
              {renderedChildren()}
            </Typography>
          ) as object;
        case 'p': {
          const blockSx = typographyBlockSx(element.attribs, 2, 1.75);
          if (paragraphContainsOnlyPlaceholders(element) || paragraphContainsOnlyImages(element)) {
            return (
              <Box component="div" sx={alignmentSxFromAttribs(element.attribs)}>
                {renderedChildren()}
              </Box>
            ) as object;
          }
          return (
            <Typography variant="body1" component="p" sx={blockSx}>
              {renderedChildren()}
            </Typography>
          ) as object;
        }

        case 'blockquote':
          return (
            <Paper
              elevation={2}
              component="blockquote"
              sx={richTextCalloutPaperSx}
            >
              {renderedChildren()}
            </Paper>
          ) as object;

        case 'hr': {
          const spacing = normalizeDividerSpacing(element.attribs?.['data-ttt-spacing']);
          return (
            <Divider variant="middle" sx={{ width: '100%', ...dividerSpacingSx(spacing) }} />
          ) as object;
        }

        case 'icon': {
          if (element.attribs.icon) {
            const iconVisual = parseIconVisualFromClass(element.attribs.class ?? '');
            const { class: _class, style: _style, icon: iconAttr, ...iconProps } =
              attributesToProps(element.attribs) as unknown as IconProps & {
                class?: string;
                style?: string;
                icon?: string;
              };
            const materialIcon = renderMaterialIconNodeFromAttr(iconAttr ?? '', element.attribs);
            if (materialIcon != null) {
              return materialIcon as object;
            }
            return (
              <Icon
                {...iconProps}
                icon={iconAttr ?? ''}
                sx={{
                  fontSize: iconVisual.fontSize,
                  color: iconVisual.color,
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
            ) as object;
          }

          switch (element.attribs.class) {
            case 'DownloadIcon':
              return (<DownloadIcon {...attributesToProps(element.attribs)} />) as object;
            case 'Icon':
              return (
                <Icon {...(attributesToProps(element.attribs) as unknown as IconProps)} />
              ) as object;
            default:
              return element;
          }
        }

        case 'img': {
          const imgProps = attributesToProps(element.attribs);
          const { className, style, ...otherProps } = imgProps;
          const classStr = typeof className === 'string' ? className : '';

          const explicitSx: Record<string, unknown> = {};
          if (element.attribs.$width) {
            explicitSx.width = element.attribs.$width;
          } else if (element.attribs.width) {
            explicitSx.width = `${element.attribs.width}px`;
          }
          if (element.attribs.$height) {
            explicitSx.height = element.attribs.$height;
          } else if (element.attribs.height) {
            explicitSx.height = `${element.attribs.height}px`;
          }
          if (element.attribs.float) {
            explicitSx.float = element.attribs.float as CSSProperties['float'];
          }
          if (element.attribs.marginleft) {
            explicitSx.marginLeft = element.attribs.marginleft;
          }
          if (element.attribs.marginright) {
            explicitSx.marginRight = element.attribs.marginright;
          }
          if (element.attribs.fit) {
            explicitSx.objectFit = element.attribs.fit as CSSProperties['objectFit'];
          }

          return (
            <RichTextImage
              src={element.attribs.src ?? ''}
              alt={element.attribs.alt ?? ''}
              className={classStr}
              explicitSx={explicitSx}
              {...otherProps}
            />
          ) as object;
        }

        case 'table':
          return (
            <Box
              sx={
                {
                  ...richTextTableOuterSx,
                  ...tableContainerSxFromAttribs(element.attribs),
                } as SxProps<Theme>
              }
            >
              <Paper elevation={2} sx={richTextTablePaperSx}>
                <TableContainer sx={richTextTableInnerContainerSx}>
                  <Table
                    size="medium"
                    sx={[richTextTableStripedSx(), tableSxFromAttribs(element.attribs)] as SxProps<Theme>}
                  >
                    {renderedChildren()}
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          ) as object;
        case 'thead':
          return <TableHead>{renderedChildren()}</TableHead> as object;
        case 'tbody':
          return <TableBody>{renderedChildren()}</TableBody> as object;
        case 'tfoot':
          return <TableFooter>{renderedChildren()}</TableFooter> as object;
        case 'caption':
          return (
            <Typography
              component="caption"
              variant="caption"
              sx={{ captionSide: 'top', display: 'table-caption', ...richTextBlockSpacingSx(1) }}
            >
              {renderedChildren()}
            </Typography>
          ) as object;
        case 'tr':
          return (
            <TableRow sx={tableRowSxFromAttribs(element.attribs)}>{renderedChildren()}</TableRow>
          ) as object;
        case 'th':
          return (
            <TableCell {...tableCellFromAttribs(element.attribs, true)}>
              {renderedChildren()}
            </TableCell>
          ) as object;
        case 'td':
          return (
            <TableCell {...tableCellFromAttribs(element.attribs)}>
              {renderedChildren()}
            </TableCell>
          ) as object;

        case 'ul':
          return (
            <Box sx={richTextListOuterSx}>
              <List
                component="ul"
                disablePadding
                sx={[richTextListRootSx(false), listSxFromAttribs(element.attribs)] as SxProps<Theme>}
              >
                {renderedChildren()}
              </List>
            </Box>
          ) as object;
        case 'ol':
          return (
            <Box sx={richTextListOuterSx}>
              <List
                component="ol"
                disablePadding
                sx={[richTextListRootSx(true), listSxFromAttribs(element.attribs)] as SxProps<Theme>}
              >
                {renderedChildren()}
              </List>
            </Box>
          ) as object;
        case 'li':
          return renderRichTextListItem(element, options) as object;

        default: {
          const materialIcon = renderMaterialIconNode(element.name, element.attribs);
          if (materialIcon != null) {
            return materialIcon as object;
          }
          return element;
        }
      }
    },
  };

  return parse(html, options);
};

;
